import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../../lib/api';
import { StaffSelect } from '../../components/StaffSelect';

interface FacilityRequest {
  id: string;
  title: string;
  description: string | null;
  category: string;
  priority: string;
  status: string;
  cost: number | string;
  location: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  user: { id: string; name: string; phone: string | null } | null;
  assignee: { id: string; name: string } | null;
  union: { id: string; name: string } | null;
}

const STATUS: Record<string, { label: string; color: 'amber' | 'navy' | 'green' | 'red' }> = {
  NEW: { label: 'جديدة', color: 'amber' },
  ASSIGNED: { label: 'مُسندة', color: 'navy' },
  IN_PROGRESS: { label: 'جارية', color: 'navy' },
  COMPLETED: { label: 'منجزة', color: 'green' },
  CANCELLED: { label: 'ملغاة', color: 'red' },
};
const PRIORITY: Record<string, { label: string; color: 'green' | 'navy' | 'amber' | 'red' }> = {
  LOW: { label: 'منخفضة', color: 'green' },
  NORMAL: { label: 'عادية', color: 'navy' },
  HIGH: { label: 'مرتفعة', color: 'amber' },
  URGENT: { label: 'عاجلة', color: 'red' },
};
const CATEGORY: Record<string, string> = {
  CLEANING: '🧹 نظافة', SECURITY: '🛡️ أمن', AC: '❄️ تكييف', ELECTRICITY: '⚡ كهرباء',
  PLUMBING: '🔧 سباكة', ELEVATOR: '🛗 مصعد', OTHER: '🔨 أخرى',
};

export function AdminFacilityPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: '', priority: '', category: '' });
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'facility', filters],
    queryFn: () => {
      // 🧹 احذف الفلاتر الفارغة لتجنّب 400 من zod
      const q: Record<string, string | number> = { limit: 50 };
      if (filters.status)   q.status = filters.status;
      if (filters.priority) q.priority = filters.priority;
      if (filters.category) q.category = filters.category;
      return api.get<Paginated<FacilityRequest>>('/api/admin/facility-requests', { query: q });
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/facility-requests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'facility'] }),
  });

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-6">🔧 طلبات المرافق والصيانة</h1>

      <Card className="mb-4">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل الحالات</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل الأولويات</option>
              {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm">
              <option value="">كل الفئات</option>
              {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody><div className="text-center py-12 text-gray-500"><div className="text-5xl mb-3">🔧</div>لا توجد طلبات</div></CardBody></Card>
      ) : (
        <div className="space-y-3">
          {data.data.map((r) => (
            <Card key={r.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span>{CATEGORY[r.category]}</span>
                      <Badge color={STATUS[r.status]?.color}>{STATUS[r.status]?.label}</Badge>
                      <Badge color={PRIORITY[r.priority]?.color}>{PRIORITY[r.priority]?.label}</Badge>
                    </div>
                    <h3 className="font-black text-navy-dark mb-1">{r.title}</h3>
                    {r.description && <p className="text-sm text-gray-600 line-clamp-2">{r.description}</p>}
                    <div className="text-xs text-gray-500 mt-2 grid grid-cols-2 gap-1">
                      <div>طالب: <b>{r.user?.name ?? '—'}</b></div>
                      {r.assignee && <div>مسند لـ: <b>{r.assignee.name}</b></div>}
                      {r.union && <div>اتحاد: <b>{r.union.name}</b></div>}
                      {r.location && <div>موقع: {r.location}</div>}
                      {Number(r.cost) > 0 && <div>تكلفة: {Number(r.cost).toLocaleString('en-US')} ر.س</div>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="primary" onClick={() => setOpenId(r.id)}>إدارة</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف الطلب؟')) deleteM.mutate(r.id); }}>🗑</Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openId && data?.data && (
        <FacilityModal
          req={data.data.find((r) => r.id === openId)!}
          onClose={() => setOpenId(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'facility'] })}
        />
      )}
    </div>
  );
}

function FacilityModal({ req, onClose, onSaved }: { req: FacilityRequest; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState(req.status);
  const [priority, setPriority] = useState(req.priority);
  const [assignedTo, setAssignedTo] = useState(req.assignee?.id ?? '');
  const [cost, setCost] = useState(req.cost?.toString() ?? '0');
  const [description, setDescription] = useState(req.description ?? '');

  const m = useMutation({
    mutationFn: () => api.put(`/api/admin/facility-requests/${req.id}`, {
      status, priority,
      assignedTo: assignedTo || null,
      cost: Number(cost),
      description,
    }),
    onSuccess: () => { onSaved(); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">{req.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الحالة</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الأولوية</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <StaffSelect label="إسناد إلى" value={assignedTo} onChange={setAssignedTo} />
            <Input label="التكلفة (ر.س)" type="number" value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الوصف / الملاحظات</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm" />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}
